import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { config } from '@/config';
import { Box } from '@radix-ui/themes';
import { ApiRequestContainer } from './api-request-container';
import {
  ApiAttribute,
  Endpoint,
  EndpointAttributes,
} from 'src/context/openapi/types';
import { ApiAttributeType } from '@components/ui/DynamicForm/DynamicForm';
import { useExplorer } from '@/hooks/useExplorer';
import { explorerAnalytics } from '@/lib/analytics';
import CodeSampleContainer from '@components/container/code-sample-container';
import { twMerge } from 'tailwind-merge';
import { RocketIcon } from 'lucide-react';
import { base64EncodedFormData } from '@/services/Base64EncodeFormData';
import { EmptyParamsToggleButton } from '@components/_components/ui/ToggleButton';
import {
  getFormattedDate,
  isAnyEndpointAttributeFilled,
  isFieldFilled,
} from '@/services/util';
import { clearForm } from '@/services/ClearForm';
import ShareForm from '@components/_components/ui/ShareForm';
import Image from 'next/image';
import binIcon from '../../../public/assets/bin-icon.svg';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@radix-ui/react-tooltip';
import { CodeSample } from '@/services/postmanCollection/postman_util';
import { convertToPCF } from '@/services/convertToPCF';
import { snakeCase, upperCase } from 'change-case-all';
import exportIcon from '../../../public/assets/export-icon.svg';
import { FormDataWithEndpointId } from '@/lib/types';
import { convertCodeSampleToEndpointAttributes } from '@/services/postmanCollection/Convertor';
import ErrorMessage from '@components/ui/ErrorMessage';
import ExportDescriptionCard from '@components/_components/ui/ExportDescriptionCard';
import { useFetchUser } from '@/auth/hooks';
import { USECASE_IN_SEARCH_PARAM } from '@/constant/Constants';

export interface ApiContainerProps {
  className?: string;
  resource: string;
  apiEndpoint: Endpoint;
  apiEndpointAttributes: EndpointAttributes;
  codeSampleContents?: string;
  codeSample?: CodeSample;
  setCodeSample: Dispatch<SetStateAction<CodeSample | undefined>>;
  setSampleResponseDisclaimerVisibility: Dispatch<SetStateAction<boolean>>;
}

export const ApiContainer: React.FC<ApiContainerProps> = ({
  className,
  resource,
  apiEndpoint,
  apiEndpointAttributes,
  codeSampleContents,
  codeSample,
  setCodeSample,
  setSampleResponseDisclaimerVisibility,
}) => {
  const isEditCodeSamplesEnabled = config.enableEditCodeSamples;
  const [isDynamicFormPopulated, setIsDynamicFormPopulated] = useState(false);
  const {
    setApi,
    endpoint,
    setEndpoint,
    endpointAttributes,
    setEndpointAttributes,
    setResponsePayload,
    responsePayload,
    setVisibleFieldsOverride,
  } = useExplorer();
  const { isLoggedIn } = useFetchUser();
  const [isEmptyAttributesVisible, setIsEmptyAttributesVisible] =
    useState(true);

  const [isScrolled, setIsScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<string[]>([]); // Error state as an array

  const addError = (error: string) => {
    setErrors((prevErrors) => [...prevErrors, error]);
  };

  const clearErrors = () => {
    setErrors([]);
  };

  const clear = () => {
    // Count filled fields before clearing
    const filledCount = endpointAttributes
      ? [
          endpointAttributes.params,
          endpointAttributes.queryParams,
          endpointAttributes.pathParameters,
        ]
          .flat()
          .filter((f) => f && isFieldFilled(f)).length
      : 0;

    explorerAnalytics.formCleared({
      resource: endpoint?.name || '',
      endpoint: endpoint?.id || '',
      fieldsFilledCount: filledCount,
      isLoggedIn: !!isLoggedIn,
    });

    if (selectedCodeSample) {
      explorerAnalytics.codeSampleCleared({
        resource: endpoint?.name || '',
        endpoint: endpoint?.id || '',
      });
    }

    localStorage.removeItem(`input_cache`);
    setResponsePayload(undefined);
    setSampleResponseDisclaimerVisibility(false);
    setSelectedCodeSample('');
    setCodeSample(undefined);

    const params = new URLSearchParams(window.location.search);
    params.delete(USECASE_IN_SEARCH_PARAM);
    const newUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);

    if (endpoint && endpointAttributes) {
      const updatedEndpointAttributes = clearForm(
        endpoint.method,
        endpointAttributes
      );
      if (updatedEndpointAttributes) {
        setEndpointAttributes({ ...updatedEndpointAttributes });
        setIsEmptyAttributesVisible(true);
      }
    }
  };

  const [isExportModalVisible, setIsExportModalVisible] = useState(false);

  const checkAndSetExportModalVisibility = () => {
    // Currently the sample response of a prebuilt code sample is shown on click of run button.
    // This creates a loophole as the request and response won't be in sync.
    // This check is to ensure that this isn't exploited.
    if (isEditCodeSamplesEnabled && !isLoggedIn) {
      addError('Login to export your code sample');
      return;
    }
    if (!responsePayload) {
      addError('No response to export');
      return;
    }
    if (JSON.stringify(responsePayload).includes('api_error_code')) {
      addError('Invalid request. Please fill in the required fields.');
      return;
    }
    setIsExportModalVisible(true);
  };

  const generatePostmanCollection = (codeSampleDescription?: string) => {
    if (endpoint && endpointAttributes) {
      const currentFormData = atob(
        base64EncodedFormData(endpointAttributes, endpoint)
      );
      if (currentFormData) {
        const formData = {
          ...JSON.parse(currentFormData),
          httpMeth: upperCase(endpoint?.method),
          desc: codeSampleDescription,
        };
        let fileName = `${snakeCase(endpoint.name)}-${getFormattedDate()}.postman_collection.json`;
        if (codeSampleDescription) {
          fileName = snakeCase(codeSampleDescription) + '-' + fileName;
        }
        return [
          convertToPCF(
            formData,
            endpoint.name,
            endpoint.name,
            (responsePayload ?? '') as unknown as string,
            fileName,
            true
          ),
          fileName,
        ];
      }
    }
    return null;
  };

  const handleExport = () => {
    if (endpoint && endpointAttributes) {
      const generatedPostmanCollection = generatePostmanCollection();
      // generatePostmanCollection function returns [collection_file, collection_file_name]
      if (generatedPostmanCollection?.length === 2) {
        const postmanCollection = generatedPostmanCollection[0];
        const fileName = generatedPostmanCollection[1] as string;
        const blob = new Blob([JSON.stringify(postmanCollection, null, 2)], {
          type: 'application/json',
        });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;

        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        explorerAnalytics.postmanExported({
          resource: endpoint?.name || '',
          endpoint: endpoint?.id || '',
          hasDescription: !!endpoint?.description,
          isLoggedIn: !!isLoggedIn,
        });
      }
      if (isEditCodeSamplesEnabled) {
        setIsExportModalVisible(false);
      }
    }
  };

  useEffect(() => {
    if (isEmptyAttributesVisible) {
      const visibleFields: Array<string> = [];
      [endpointAttributes?.params, endpointAttributes?.queryParams].forEach(
        (endpointAttribute) => {
          endpointAttribute?.forEach((field) => {
            if (isFieldFilled(field)) {
              visibleFields.push(field.key);
            }
          });
        }
      );
      setVisibleFieldsOverride([...visibleFields]);
    }
  }, [endpointAttributes, isEmptyAttributesVisible]);

  useEffect(() => {
    if (
      endpointAttributes &&
      !isAnyEndpointAttributeFilled(endpointAttributes)
    ) {
      setIsEmptyAttributesVisible(true);
    }
  }, [endpointAttributes]);
  const [selectedCodeSample, setSelectedCodeSample] = useState<string>('');

  useEffect(() => {
    setApi(resource);
    setEndpoint(apiEndpoint);
    setEndpointAttributes(apiEndpointAttributes);
    setResponsePayload(undefined);

    if (apiEndpoint) {
      explorerAnalytics.playgroundPageViewed({
        resource,
        endpointName: apiEndpoint.id || '',
        httpMethod: apiEndpoint.method || '',
        hasCodeSamples: !!codeSampleContents,
        isLoggedIn: !!isLoggedIn,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource, apiEndpoint, apiEndpointAttributes]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollContainerRef.current) {
        setIsScrolled(scrollContainerRef.current.scrollTop > 50);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      if (!scrollContainerRef.current || !e.target) return;

      if (scrollContainerRef.current.contains(e.target as Node)) {
        const focusedElement = e.target as HTMLElement;
        const containerRect =
          scrollContainerRef.current.getBoundingClientRect();
        const elementRect = focusedElement.getBoundingClientRect();

        if (elementRect.bottom > containerRect.bottom) {
          scrollContainerRef.current.scrollBy({
            top: elementRect.bottom - containerRect.bottom + 20,
            behavior: 'smooth',
          });
        }

        if (elementRect.top < containerRect.top) {
          scrollContainerRef.current.scrollBy({
            top: elementRect.top - containerRect.top - 20,
            behavior: 'smooth',
          });
        }
      }
    };

    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, []);

  const handleParamUpdate = useCallback(
    (params: ApiAttribute[], type: ApiAttributeType) => {
      if (!endpointAttributes || !endpoint) {
        return;
      }
      const updatedEndpointAttributes: EndpointAttributes = {
        ...endpointAttributes,
      };
      switch (type) {
        case ApiAttributeType.BODY:
          updatedEndpointAttributes.params = params;
          break;
        case ApiAttributeType.QUERY:
          updatedEndpointAttributes.queryParams = params;
          break;
        case ApiAttributeType.PATH:
          updatedEndpointAttributes.pathParameters = params;
          break;
      }
      setEndpointAttributes({ ...updatedEndpointAttributes });
      // ToDo: Handle edge cases
      // 1. Which should take priority query_params or local_storage
      // localStorage.setItem(
      //   `input_cache`,
      //   atob(base64EncodedFormData(updatedEndpointAttributes, endpoint))
      // );
    },
    [endpointAttributes, setEndpointAttributes]
  );

  useEffect(() => {
    if (!isDynamicFormPopulated && endpoint && endpointAttributes) {
      const cachedFormData = localStorage.getItem(`input_cache`);
      if (cachedFormData) {
        const cachedFormDataJson = JSON.parse(
          cachedFormData
        ) as FormDataWithEndpointId;
        if (cachedFormDataJson.endpointId === endpoint.id) {
          const updatedAttributes = convertCodeSampleToEndpointAttributes(
            endpoint.method,
            endpoint.url,
            endpointAttributes,
            cachedFormDataJson
          );
          if (updatedAttributes) {
            setEndpointAttributes({ ...updatedAttributes });
          }
          setIsDynamicFormPopulated(true);
        }
      }
    }
  }, [
    endpoint,
    endpointAttributes,
    isDynamicFormPopulated,
    setEndpointAttributes,
  ]);

  const formData = () => {
    const currentUrl = `${window.location.origin}${window.location.pathname}`;
    if (endpointAttributes && endpoint) {
      return `${currentUrl}?params=
      ${encodeURIComponent(base64EncodedFormData(endpointAttributes, endpoint))}`;
    }
    return currentUrl;
  };

  return (
    <Box className="w-full">
      <Box
        className={twMerge(
          className,
          'pt-3 overflow-y-hidden flex flex-col h-full'
        )}
      >
        <div className="sticky top-0 px-4 z-10">
          <div
            className={twMerge(
              'transition-all duration-300 ease-in-out overflow-hidden grid',
              isScrolled ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
            )}
          >
            <div className="min-h-0">
              <div className="tracking-tight text-[#012A38] text-[16px]">
                {endpoint?.description}
              </div>
            </div>
          </div>
        </div>

        <div
          ref={scrollContainerRef}
          className="scrollbar-hide mt-4 px-4 pb-10 h-full overflow-y-auto"
        >
          {codeSampleContents && (
            <CodeSampleContainer
              selectedCodeSample={selectedCodeSample}
              setSelectedCodeSample={setSelectedCodeSample}
              codeSample={codeSample}
              setCodeSample={setCodeSample}
              rawCodeSample={codeSampleContents}
              setIsEmptyAttributesVisible={setIsEmptyAttributesVisible}
            />
          )}
          {apiEndpointAttributes && (
            <Box className="mb-48">
              <ApiRequestContainer
                options={{
                  endpointAttributes: apiEndpointAttributes,
                  url: apiEndpoint?.url,
                }}
                isEmptyAttributesVisible={isEmptyAttributesVisible}
                updateParams={handleParamUpdate}
                formActions={
                  <TooltipProvider>
                    <Box className="flex items-center gap-2">
                      {isEditCodeSamplesEnabled &&
                        isAnyEndpointAttributeFilled(apiEndpointAttributes) && (
                          <>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="flex items-center gap-1 font-inter text-[12px] font-medium cursor-pointer transition-colors duration-150"
                                  onClick={checkAndSetExportModalVisibility}
                                >
                                  <RocketIcon size={15} />
                                  Contribute Sample
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="bg-gray-700 text-white px-2 py-1 rounded-md text-xs max-w-52 break-words">
                                Suggest a new code sample for this endpoint
                              </TooltipContent>
                            </Tooltip>
                            <div className="w-[1px] h-4 bg-[#A2C1C4]" />
                          </>
                        )}
                      {isAnyEndpointAttributeFilled(apiEndpointAttributes) && (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="flex items-center gap-1 text-[#012A38] font-inter text-[12px] font-normal cursor-pointer"
                                onClick={handleExport}
                              >
                                <Image src={exportIcon} alt="export_icon" />
                                Export
                              </button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-700 text-white px-2 py-1 rounded-md text-xs">
                              Export as Postman Collection
                            </TooltipContent>
                          </Tooltip>

                          <div className="w-[1px] h-4 bg-[#A2C1C4]" />
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <ShareForm
                                formData={formData()}
                                onShared={() => {
                                  explorerAnalytics.formShared({
                                    resource: endpoint?.name || '',
                                    endpoint: endpoint?.id || '',
                                    isLoggedIn: !!isLoggedIn,
                                  });
                                }}
                              />
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-700 text-white px-2 py-1 rounded-md text-xs">
                              Copy form as shareable link{' '}
                            </TooltipContent>
                          </Tooltip>

                          <div className="w-[1px] h-4 bg-[#A2C1C4]" />

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <EmptyParamsToggleButton
                                  isVisible={isEmptyAttributesVisible}
                                  setIsVisible={setIsEmptyAttributesVisible}
                                  onToggle={(visible) => {
                                    explorerAnalytics.emptyFieldsToggled({
                                      isVisible: visible,
                                      resource: endpoint?.name || '',
                                      endpoint: endpoint?.id || '',
                                      isLoggedIn: !!isLoggedIn,
                                    });
                                  }}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="bg-gray-700 text-white px-2 py-1 rounded-md text-xs">
                              Show/Hide empty fields
                            </TooltipContent>
                          </Tooltip>

                          <div className="w-[1px] h-4 bg-[#A2C1C4]" />
                        </>
                      )}

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center gap-1 text-[#012A38] font-inter text-[12px] font-normal cursor-pointer"
                            onClick={clear}
                          >
                            <Image src={binIcon} alt="bin_icon" />
                            Clear
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-700 text-white px-2 py-1 rounded-md text-xs">
                          Clear form
                        </TooltipContent>
                      </Tooltip>
                    </Box>
                  </TooltipProvider>
                }
              />

              {/*  Render "Show All Fields" button at the end of the form to make it more apparent that
            we're showing only the filled in fields */}
              {!isEmptyAttributesVisible && (
                <Box className="flex justify-center mt-8">
                  <EmptyParamsToggleButton
                    isVisible={isEmptyAttributesVisible}
                    setIsVisible={setIsEmptyAttributesVisible}
                    onToggle={(visible) => {
                      explorerAnalytics.emptyFieldsToggled({
                        isVisible: visible,
                        resource: endpoint?.name || '',
                        endpoint: endpoint?.id || '',
                        isLoggedIn: !!isLoggedIn,
                      });
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </div>
      </Box>

      {errors.length > 0 && (
        <ErrorMessage messages={errors} clearErrors={clearErrors} />
      )}

      {isEditCodeSamplesEnabled && isExportModalVisible && (
        <ExportDescriptionCard
          isVisible={isExportModalVisible}
          setIsVisible={setIsExportModalVisible}
          generatePostmanCollection={generatePostmanCollection}
        />
      )}
    </Box>
  );
};
